import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Formik, Form, Field } from 'formik';
import makeStyles from '@mui/styles/makeStyles';
import { green, red } from '@mui/material/colors';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Tooltip from '@mui/material/Tooltip';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import { DialogTitleWithClose } from 'ui';

import { toBase64 } from 'shared/functions';
import { editProfileSchema } from 'shared/validations';
import { IMAGE_MAX_SIZE, IMAGE_ACCEPT_TYPE } from 'shared/constants/globalConstants';
import { fetchUserImage, fetchJobFunctions } from 'services';
import { addNotificationAction } from 'store/notifications/notificationsActions';
import { editProfileAction } from 'store/login/loginAction';

const useStyles = makeStyles(theme => ({
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    color: '#fff',
    backgroundColor: red[400],
    padding: theme.spacing(0.5),
    opacity: 0.5,
  },
  profilePictureLabel: {
    marginBottom: theme.spacing(2),
  },
  profilePictureDropzone: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 160,
    height: 160,
    border: `2px dashed ${theme.palette.grey[200]}`,
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    textAlign: 'center',

    '&:hover': {
      border: `2px dashed ${theme.palette.primary.main}`,
    },
  },
  profilePictureActive: {
    backgroundColor: green[100],
    border: `2px dashed ${green[600]}`,
  },
  profilePictureReject: {
    backgroundColor: red[100],
    border: `2px dashed ${red[600]}`,
  },
  profilePictureContainer: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    borderRadius: 2,
    width: 160,
    height: 160,
    boxSizing: 'border-box',
  },
  profilePicture: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'cover',
    borderRadius: theme.shape.borderRadius,
  },
}));

const EditProfileModal = ({ open, onClose }) => {
  const didUnmount = React.useRef(false);
  const classes = useStyles();
  const dispatch = useDispatch();
  const [file, setFile] = useState([]);
  const { t } = useTranslation();
  const [jobFunctions, setJobFunctions] = useState([]);
  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    accept: IMAGE_ACCEPT_TYPE,
    maxSize: IMAGE_MAX_SIZE,
    minSize: 0,
    multiple: false,
    onDrop: files =>
      !!files.length &&
      setFile(
        Object.assign(files[0], {
          preview: URL.createObjectURL(files[0]),
        }),
      ),
    onDropRejected: files => {
      if (files[0].size > IMAGE_MAX_SIZE) {
        dispatch(
          addNotificationAction({
            message: t('File max size is 2MB. Please load another one.'),
            variant: 'error',
          }),
        );
      }
      if (files[0].type !== IMAGE_ACCEPT_TYPE) {
        dispatch(
          addNotificationAction({
            message: t('Unexpected file type. Please load JPEG instead.'),
            variant: 'error',
          }),
        );
      }
    },
  });
  const { data } = useSelector(state => state.auth);
  const { user } = data;

  const fetchImage = async () => {
    let fileValue = [];
    try {
      const url = await fetchUserImage(data.userId, true);
      const tempFile = new File([url], 'User picture');

      fileValue = Object.assign(tempFile, {
        preview: URL.createObjectURL(url),
      });
    } catch (e) {
      // do nothing
    } finally {
      if (!didUnmount.current) {
        setFile(fileValue);
      }
    }
  };

  useEffect(() => {
    if (data.userId) fetchImage();
    return () => {
      didUnmount.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const initialValues = {
    firstName: data.firstName,
    name: data.name,
    phone: user && user.phone,
    email: user && user.email,
    jobTitle: user && user.jobTitle,
    jobFunction: user && user.jobFunction,
  };

  useEffect(() => {
    async function fetchData() {
      const response = await fetchJobFunctions();
      if (!didUnmount.current) {
        setJobFunctions(response);
      }
    }

    fetchData();
  }, [user]);

  const handleSubmit = async (values, { setSubmitting }) => {
    const handledValues = {
      ...values,
      avatar: '',
    };

    if (file.length !== 0) {
      const base64 = await toBase64(file);
      const [, avatar] = base64.split(',');
      handledValues.avatar = avatar;
    }

    dispatch(editProfileAction(handledValues));
    setSubmitting(true);
    onClose();
    dispatch(
      addNotificationAction({
        message: t('Profile was successfully updated.'),
        variant: 'success',
      }),
    );
  };

  const removePicture = () => {
    setFile([]);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitleWithClose onClose={onClose}>{t('Edit profile')}</DialogTitleWithClose>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={editProfileSchema}
      >
        {({ errors, isSubmitting, touched }) => (
          <Form noValidate>
            <DialogContent>
              <Field name="firstName">
                {({ field }) => (
                  <TextField
                    {...field}
                    type="text"
                    label="First name"
                    error={errors.firstName && touched.firstName}
                    fullWidth
                    required
                  />
                )}
              </Field>
              <Field name="name">
                {({ field }) => (
                  <TextField
                    {...field}
                    type="text"
                    label="Last name"
                    error={errors.name && touched.name}
                    fullWidth
                    required
                  />
                )}
              </Field>
              <Field name="phone">
                {({ field }) => (
                  <TextField
                    {...field}
                    type="tel"
                    label="Telephone"
                    error={errors.phone && touched.phone}
                    fullWidth
                    required
                  />
                )}
              </Field>
              <Field name="email">
                {({ field }) => (
                  <TextField
                    {...field}
                    type="email"
                    label="E-mail address"
                    error={errors.email && touched.email}
                    fullWidth
                    required
                    disabled
                  />
                )}
              </Field>
              <Field name="jobTitle">
                {({ field }) => (
                  <TextField
                    {...field}
                    type="text"
                    label="Job title"
                    error={errors.jobTitle && touched.jobTitle}
                    fullWidth
                    required
                  />
                )}
              </Field>
              <Field name="jobFunction">
                {({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Job function"
                    fullWidth
                    required
                    SelectProps={{
                      native: true,
                    }}
                  >
                    {jobFunctions.map(item => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </TextField>
                )}
              </Field>
              <div className={classes.profilePicture}>
                <InputLabel className={classes.profilePictureLabel}>
                  {t('Profile picture')}
                </InputLabel>
                {file.length === 0 && (
                  <div
                    {...getRootProps({
                      className: clsx(
                        isDragActive && classes.profilePictureActive,
                        isDragReject && classes.profilePictureReject,
                        classes.profilePictureDropzone,
                      ),
                    })}
                  >
                    <input {...getInputProps()} />
                    <Typography variant="body2" color="textSecondary" component="p">
                      {isDragReject
                        ? t('File will be rejected')
                        : t('Drop image or click to browse')}
                    </Typography>
                  </div>
                )}
                {file.preview && (
                  <aside className={classes.profilePictureContainer}>
                    <img
                      src={file.preview}
                      className={classes.profilePicture}
                      alt={t('Profile picture')}
                    />
                    <Tooltip title={t('Remove current profile picture')}>
                      <IconButton
                        className={classes.closeButton}
                        aria-label="close"
                        onClick={removePicture}
                        size="large">
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </aside>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" onClick={onClose}>
                {t('Cancel')}
              </Button>
              <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                {t('Save')}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

EditProfileModal.defaultProps = {
  open: false,
  onClose: () => null,
};

EditProfileModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default EditProfileModal;
